/**
 * Radio-billing sub-fields for a single sales-order line item.
 *
 * Rendered under an item row when the product carries radios: picks the
 * billing model (prepaid / shipped / date), the prepaid service, price and
 * term, the billing start date and any access-fee end-date preview, the
 * trial period, and — once the order leaves Draft — the per-radio ICCID
 * entry grid with paste-to-fill.
 *
 * Contains:
 * - RadioBillingFields — memoized line-item radio-billing field group.
 */
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { addMonths, format, lastDayOfMonth } from 'date-fns';
import { Field } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { FormikCurrencyTextField } from '@sophocles/components/CurrencyTextField';
import ProductAutocomplete from '@sophocles/components/ProductAutocomplete';

function getAccessFeeEndDateString(startDate, months) {
  if (!startDate || !months) return '';
  const start = new Date(startDate);
  const end = addMonths(start, months);
  const endOfMonth = lastDayOfMonth(end);
  return `Through ${format(endOfMonth, 'MMM d, yyyy')}`;
}

const RadioBillingFields = React.memo(function RadioBillingFields({
  index,
  item,
  products,
  customerCatalog,
  values,
  errors,
  touched,
  setFieldValue,
  isDisabledForStatus,
}) {
  const { t } = useTranslation();
  // Determine if the main item is a prepaid service
  const mainProduct = products.find(p => p.id === item.item);
  const isMainPrepaid = mainProduct?.is_service && mainProduct?.has_radios;
  const isDynamicTerm = isMainPrepaid && !mainProduct?.billing_term_months;

  // If main item is prepaid, force radio_billing_type to 'Prepaid' and auto-fill prepaid fields
  React.useEffect(() => {
    if (isMainPrepaid) {
      if (item.radio_billing_type !== 'Prepaid') {
        setFieldValue(`order_sale_items.${index}.radio_billing_type`, 'Prepaid');
      }
      // Set prepaid_service, prepaid_cost (0), and term_length_prepaid from main product
      if (item.prepaid_service !== mainProduct.id) {
        setFieldValue(`order_sale_items.${index}.prepaid_service`, mainProduct.id);
      }
      if (item.prepaid_cost !== 0) {
        setFieldValue(`order_sale_items.${index}.prepaid_cost`, 0);
      }
      // Only auto-fill term_length_prepaid when the product has a fixed term
      if (!isDynamicTerm && item.term_length_prepaid !== mainProduct.billing_term_months) {
        setFieldValue(`order_sale_items.${index}.term_length_prepaid`, mainProduct.billing_term_months);
      }
    }
  }, [isMainPrepaid, isDynamicTerm, item, mainProduct, index, setFieldValue]);

  const prepaidServiceProduct = products.find(p => p.id === item.prepaid_service);
  const showAccessFee =
    (isMainPrepaid || item.radio_billing_type === 'Prepaid') && prepaidServiceProduct?.is_access_fee;
  const isServiceDynamicTerm =
    !isDynamicTerm && item.radio_billing_type === 'Prepaid' && !prepaidServiceProduct?.billing_term_months;

  return (
    <Box
      sx={{
        backgroundColor: 'var(--surface-secondary)',
        p: 'var(--space-3)',
        borderBottomLeftRadius: 'var(--radius-md)',
        borderBottomRightRadius: 'var(--radius-md)',
      }}
    >
      <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="flex-start">
        {/* Term Select - only show if not main prepaid */}
        {!isMainPrepaid && (
          <Box sx={{ minWidth: 160, flex: '1 1 auto' }}>
            <FormControl fullWidth size="small" required error={!!errors.order_sale_items?.[index]?.radio_billing_type}>
              <InputLabel>{t('warehouse.term')}</InputLabel>
              <Field
                name={`order_sale_items.${index}.radio_billing_type`}
                as={Select}
                label={t('general.type', 'Type')}
                fullWidth
                disabled={isDisabledForStatus(values.status, 'item')}
              >
                <MenuItem value="Prepaid">{t('warehouse.billingTypePrepaid')}</MenuItem>
                <MenuItem value="Shipped">{t('warehouse.billingTypeShipped')}</MenuItem>
                <MenuItem value="Date">{t('warehouse.billingTypeDate')}</MenuItem>
              </Field>
              <FormHelperText>{errors.order_sale_items?.[index]?.radio_billing_type ?? ' '}</FormHelperText>
            </FormControl>
          </Box>
        )}

        {/* Prepaid Fields - only show if selected prepaid */}
        {!isMainPrepaid && item.radio_billing_type === 'Prepaid' && (
          <>
            <Box sx={{ minWidth: 200, flex: '1 1 auto' }}>
              <ProductAutocomplete
                fullWidth
                required
                label={t('warehouse.prepaidService')}
                name={`order_sale_items.${index}.prepaid_service`}
                size="small"
                value={item.prepaid_service}
                filter={p => p.is_service}
                helperText={
                  touched.order_sale_items?.[index]?.prepaid_service &&
                  errors.order_sale_items?.[index]?.prepaid_service
                }
                onChange={p => {
                  setFieldValue(`order_sale_items.${index}.prepaid_service`, p.id);
                  setFieldValue(`order_sale_items.${index}.prepaid_cost`, p.item_price);
                  setFieldValue(`order_sale_items.${index}.term_length_prepaid`, p.billing_term_months ?? 0);
                }}
                disabled={isDisabledForStatus(values.status, 'prepaid_service')}
              />
            </Box>

            <Box sx={{ minWidth: 160, flex: '1 1 auto' }}>
              <Field
                name={`order_sale_items.${index}.prepaid_cost`}
                as={FormikCurrencyTextField}
                label={t('warehouse.prepaidPrice')}
                fullWidth
                size="small"
                type="number"
                variant="outlined"
                error={
                  touched.order_sale_items?.[index]?.prepaid_cost &&
                  Boolean(errors.order_sale_items?.[index]?.prepaid_cost)
                }
                helperText={errors.order_sale_items?.[index]?.prepaid_cost ?? ' '}
                disabled={isDisabledForStatus(values.status, 'price')}
              />
            </Box>
          </>
        )}

        {/* Start Date - show for Prepaid, Date, or when trial is selected (non-Shipped) */}
        {(isMainPrepaid ||
          item.radio_billing_type === 'Prepaid' ||
          item.radio_billing_type === 'Date' ||
          (item.term_length_trial > 0 && item.radio_billing_type !== 'Shipped')) && (
          <Box sx={{ minWidth: 160, flex: '1 1 auto' }}>
            <Field
              name={`order_sale_items.${index}.radio_billing_start_at`}
              as={TextField}
              label={t('warehouse.billingStartDate')}
              type="date"
              fullWidth
              size="small"
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              error={
                touched.order_sale_items?.[index]?.radio_billing_start_at &&
                Boolean(errors.order_sale_items?.[index]?.radio_billing_start_at)
              }
              helperText={errors.order_sale_items?.[index]?.radio_billing_start_at ?? ' '}
              disabled={isDisabledForStatus(values.status, 'price')}
            />
          </Box>
        )}

        {/* Access Fee / Dynamic Term Fields */}
        {(showAccessFee || isDynamicTerm || isServiceDynamicTerm) && (
          <>
            <Box sx={{ minWidth: 100, flex: '1 1 auto' }}>
              <Field
                name={`order_sale_items.${index}.term_length_prepaid`}
                as={TextField}
                label={t('general.months')}
                fullWidth
                size="small"
                type="number"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                disabled={isDisabledForStatus(values.status, 'item')}
              />
            </Box>
            <Box sx={{ minWidth: 220, flex: '1 1 auto', mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {getAccessFeeEndDateString(
                  values.order_sale_items?.[index]?.radio_billing_start_at,
                  values.order_sale_items?.[index]?.term_length_prepaid,
                )}
              </Typography>
            </Box>
          </>
        )}

        {/* Show end date for fixed-term prepaid items */}
        {!showAccessFee &&
          !isDynamicTerm &&
          (isMainPrepaid || item.radio_billing_type === 'Prepaid') &&
          item.term_length_prepaid > 0 && (
            <Box sx={{ minWidth: 220, flex: '1 1 auto', mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {getAccessFeeEndDateString(
                  values.order_sale_items?.[index]?.radio_billing_start_at,
                  values.order_sale_items?.[index]?.term_length_prepaid,
                )}
              </Typography>
            </Box>
          )}

        {/* Prepaid Follows Radio - only show for prepaid items */}
        {item.prepaid_service && (
          <Box sx={{ minWidth: 220, flex: '1 1 auto', display: 'flex', alignItems: 'center' }}>
            <Tooltip
              title={item.prepaid_follows_radio_editable === false ? t('warehouse.radiosCreatedTooltip') : ''}
              placement="top"
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={item.prepaid_follows_radio || false}
                    onChange={e => {
                      setFieldValue(`order_sale_items.${index}.prepaid_follows_radio`, e.target.checked);
                    }}
                    disabled={item.prepaid_follows_radio_editable === false}
                    size="small"
                  />
                }
                label={<Typography variant="body2">{t('warehouse.prepaidFollowsRadio')}</Typography>}
              />
            </Tooltip>
          </Box>
        )}

        {/* Trial Term (non-Prepaid) - only show if not main prepaid */}
        {!isMainPrepaid && item.radio_billing_type !== 'Prepaid' && (
          <Box sx={{ minWidth: 160, flex: '1 1 auto' }}>
            <FormControl fullWidth size="small" required error={!!errors.order_sale_items?.[index]?.term_length_trial}>
              <InputLabel>{t('warehouse.trialTerm')}</InputLabel>
              <Field
                name={`order_sale_items.${index}.term_length_trial`}
                as={Select}
                label={t('warehouse.trialPeriod')}
                fullWidth
                size="small"
                disabled={isDisabledForStatus(values.status, 'item')}
              >
                <MenuItem value="0">{t('warehouse.noTrial')}</MenuItem>
                <MenuItem value="30">{t('warehouse.trialDays', { days: 30 })}</MenuItem>
                <MenuItem value="60">{t('warehouse.trialDays', { days: 60 })}</MenuItem>
                <MenuItem value="90">{t('warehouse.trialDays', { days: 90 })}</MenuItem>
              </Field>
              <FormHelperText>{t('warehouse.trialStartsWithBilling')}</FormHelperText>
            </FormControl>
          </Box>
        )}
      </Stack>
      {values.status !== 'Draft' && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1">{t('warehouse.iccidEntry')}</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2} alignItems="center">
            {item.iccids &&
              item.iccids.length > 0 &&
              item.iccids.map((iccid, iccidIndex) => {
                const iccidDisabled = isDisabledForStatus(values.status, 'iccid');
                return (
                  <Grid size={3} key={iccidIndex}>
                    <Field
                      name={`order_sale_items.${index}.iccids.${iccidIndex}.iccid`}
                      as={TextField}
                      label={`ICCID ${iccidIndex + 1}`}
                      fullWidth
                      onPaste={e => {
                        // Disabled fields must not accept paste — the onPaste
                        // handler writes via setFieldValue and would otherwise
                        // bypass the input's disabled state.
                        if (iccidDisabled) {
                          e.preventDefault();
                          return;
                        }
                        // Get the pasted text and split by line feeds
                        const pastedData = e.clipboardData.getData('Text').split('\n');
                        // Update ICCID fields with each line as a separate entry
                        pastedData.forEach((text, i) => {
                          const targetIndex = iccidIndex + i;
                          if (targetIndex < item.iccids.length) {
                            setFieldValue(`order_sale_items.${index}.iccids.${targetIndex}.iccid`, text.trim());
                          }
                        });
                        e.preventDefault(); // Prevent default pasting behavior
                      }}
                      onKeyPress={e => {
                        // ASCII 10 corresponds to LF (Line Feed)
                        if (e.which === 10 || e.keyCode === 10 || e.key === 'Enter') {
                          // Prevent default behavior to avoid form submission
                          e.preventDefault();

                          // Move focus to the next ICCID field
                          const nextIccidIndex = iccidIndex + 1;
                          if (nextIccidIndex < item.iccids.length) {
                            const nextField = document.querySelector(
                              `input[name="order_sale_items.${index}.iccids.${nextIccidIndex}.iccid"]`,
                            );
                            nextField?.focus();
                          }
                        }
                      }}
                      disabled={iccidDisabled}
                      error={
                        touched.order_sale_items?.[index]?.iccids?.[iccidIndex]?.iccid &&
                        Boolean(errors.order_sale_items?.[index]?.iccids?.[iccidIndex]?.iccid)
                      }
                      helperText={
                        touched.order_sale_items?.[index]?.iccids?.[iccidIndex]?.iccid &&
                        errors.order_sale_items?.[index]?.iccids?.[iccidIndex]?.iccid
                      }
                    />
                  </Grid>
                );
              })}
          </Grid>
        </Box>
      )}
    </Box>
  );
});

export default RadioBillingFields;
